import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Button, Dropdown, Form } from '@bynder/design-system';
import { fontsElementsSelector } from 'packages/store/creativeEditor/creativeEditor.selectors';
import generateTestId from 'packages/helpers/testIdHelpers';
import { fontStyleName } from 'packages/pages/brandFonts/components/styleName';
import type { ExtendedFont, FontFamily as FontFamilyType } from '../../Shots/Text/Fonts/types';

type Props = {
    fontStyleData: [string, boolean, string];
    fontId: string;
    onFontStyleChange: (fontId: string) => void;
    disabled?: boolean;
    locked?: boolean;
};

const matchesId = (font: ExtendedFont, id: string) =>
    font.referenceIds.some((referenceId) => String(referenceId) === id);

const FontStyleBase = ({ fontStyleData, fontId, onFontStyleChange, disabled, locked }: Props) => {
    const [currentFontId, isMixed, mixedValue] = fontStyleData;
    const fonts: FontFamilyType[] = useSelector(fontsElementsSelector);

    const { family, currentStyleName } = useMemo(() => {
        for (const candidate of fonts) {
            const match = candidate.fonts.find((f) => matchesId(f, fontId));

            if (match) {
                const current = candidate.fonts.find((f) => matchesId(f, currentFontId)) ?? match;

                return { family: candidate, currentStyleName: fontStyleName(candidate, current) };
            }
        }

        return { family: undefined, currentStyleName: '' };
    }, [fonts, fontId, currentFontId]);
    const familyFonts = family?.fonts ?? [];

    const displayValue = isMixed ? mixedValue : currentStyleName;

    const handleChange = useCallback(
        (font: ExtendedFont) => {
            onFontStyleChange(String(font.id));
        },
        [onFontStyleChange],
    );

    return (
        <Form.Group>
            <Dropdown
                {...generateTestId('shots_text_style')}
                trigger={({ isOpen, ...triggerProps }) => (
                    <Button
                        aria-label="shots_text_style"
                        isPressed={isOpen}
                        isDisabled={disabled || locked || familyFonts.length <= 1}
                        rightIcon={<Dropdown.Arrow />}
                        isFullWidth
                        {...triggerProps}
                    >
                        <Dropdown.SelectButtonText>{displayValue}</Dropdown.SelectButtonText>
                    </Button>
                )}
                position="bottom"
            >
                {family &&
                    family.fonts.map((font) => (
                        <Dropdown.Item
                            key={font.id}
                            isChecked={matchesId(font, currentFontId)}
                            onClick={() => handleChange(font)}
                        >
                            {fontStyleName(family, font)}
                        </Dropdown.Item>
                    ))}
            </Dropdown>
        </Form.Group>
    );
};

export { FontStyleBase };
